import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { env } from '@/env';
import { ritentaSoloLeLetture } from '@/lib/ritentaSoloLeLetture';
import { guardiaOspite } from '@/services/guardiaOspite';
import { getQchessAccessToken } from '@/lib/logto';
import type {
  Bracket,
  Challenge,
  CreateChessChallengeRequest,
  CreateTournamentRequest,
  LeaderboardQuery,
  LeaderboardResponse,
  LichessLinkStartResponse,
  LichessLinkStatus,
  ListBracketsQuery,
  ListBracketsResponse,
  ListChessChallengesQuery,
  ListChallengesResponse,
  ListTournamentsQuery,
  ListTournamentsResponse,
  Tournament,
  TournamentResultsResponse,
} from '@/types/qchess';

/**
 * QChess API client. Talks to qchess-api at `/v1/*`.
 *
 * Auth: Logto Bearer JWT with audience `https://qchess.qlash.gg/api`, minted
 * by getQchessAccessToken() through the shared serialized refresh-token
 * redemption (same pattern as qriots/qbot/qsocials/qduels). qchess-api
 * validates the JWT `aud` against exactly that audience and the issuer against
 * `https://account.qlash.gg/oidc`.
 *
 * ⚠️ Logto app grant required: minting a token for the qchess audience only
 * works if the TMA's Logto application has the "Qlash API QChess" API resource
 * granted server-side (Logto console → Applications → <TMA app> → API
 * resources). If that grant is missing, getQchessAccessToken() resolves to
 * null (refresh grant → HTTP 400), no Authorization header is attached, and
 * qchess answers 401. This is the one config step the Mini App can't
 * self-provision — coordinate with Enrico, same as qduels.
 *
 * Dev: env.qchessApiUrl = '/qchess-api', Vite proxy forwards to the MacMini
 * (`http://100.94.221.52:3600`).
 * Prod: env.qchessApiUrl = 'https://qchess.qlash.gg'.
 *
 * Like qduels, qchess responses are NOT wrapped in a `{ data }` / `{ success }`
 * envelope — POST endpoints return the object directly (HTTP 201) and errors
 * return `{ error: "<code>" }`.
 *
 * NB (dev auth): qchess-api also accepts the dev-bypass headers
 * `X-Dev-Bypass` + `X-Dev-User` in NODE_ENV=development, but the TMA has no
 * dev-bypass mechanism wired anywhere (qriots/qduels/qbot all use the Logto
 * Bearer + Vite proxy path). We replicate that established pattern; running in
 * browser dev mode against the MacMini works once the user has a Logto session
 * and the QChess resource is granted.
 */
export const qchessApi = createApi({
  reducerPath: 'qchessApi',
  // ⛔ Una SCRITTURA non si ritenta MAI (un `createTournament` ripetuto e' un secondo torneo); una LETTURA UNA volta,
  // solo su rete/timeout/5xx, mai su un 4xx (non linkato, cannot_challenge_self, 401: deterministici, un ritentativo
  // ritarda solo l'errore). La regola e' `lib/ritentaSoloLeLetture.ts`, provata in `tests/ritentativi-altri-client.test.tsx`.
  baseQuery: guardiaOspite('qchess', ritentaSoloLeLetture(
    fetchBaseQuery({
      baseUrl: env.qchessApiUrl,
      prepareHeaders: async (headers) => {
        const token = await getQchessAccessToken();
        if (token) headers.set('Authorization', `Bearer ${token}`);
        headers.set('x-platform-id', env.platformId);
        headers.set('Accept', 'application/json');
        headers.set('Content-Type', 'application/json');
        return headers;
      },
    }),
  )),
  // 'Link' tracks the caller's Lichess link (GET /v1/link); unlink invalidates
  // it. 'Tournaments' / 'Challenges' track the respective lists so create /
  // accept / decline flip them without a manual refetch.
  tagTypes: ['Link', 'Tournaments', 'Challenges', 'Brackets'],
  endpoints: (builder) => ({
    /* ── Lichess link ───────────────────────────────────────────────── */

    /**
     * The caller's current Lichess link (GET /v1/link). `linked: false` is the
     * "not linked yet" state returned with HTTP 200 — NOT an error — so the
     * home screen can branch on `data.linked` directly.
     */
    getLink: builder.query<LichessLinkStatus, void>({
      query: () => '/v1/link',
      providesTags: ['Link'],
    }),

    /**
     * Begin the Lichess OAuth link (GET /v1/link/lichess/start). Returns an
     * `authorizeUrl` the caller opens in the system browser via Telegram
     * `openLink`. After consent the browser hits the qchess callback and the
     * account is linked; the caller then re-fetches `getLink`.
     */
    startLichessLink: builder.query<LichessLinkStartResponse, void>({
      query: () => '/v1/link/lichess/start',
      // Never cache — each start mints a fresh OAuth state/PKCE on the server.
      keepUnusedDataFor: 0,
    }),

    /** Unlink the Lichess account (DELETE /v1/link/lichess → 204). */
    unlinkLichess: builder.mutation<void, void>({
      query: () => ({ url: '/v1/link/lichess', method: 'DELETE' }),
      invalidatesTags: ['Link'],
    }),

    /* ── Tournaments ────────────────────────────────────────────────── */

    /**
     * List chess tournaments (GET /v1/tournaments). Optional status filter +
     * paging. Returns `{ tournaments: [...] }`.
     */
    listTournaments: builder.query<ListTournamentsResponse, ListTournamentsQuery | void>({
      query: (params) => ({
        url: '/v1/tournaments',
        params: params
          ? {
              ...(params.status ? { status: params.status } : {}),
              ...(params.page != null ? { page: params.page } : {}),
              ...(params.limit != null ? { limit: params.limit } : {}),
            }
          : undefined,
      }),
      providesTags: ['Tournaments'],
    }),

    /** Single tournament by Lichess id (GET /v1/tournaments/:id). */
    getTournament: builder.query<Tournament, string>({
      query: (id) => `/v1/tournaments/${encodeURIComponent(id)}`,
      providesTags: ['Tournaments'],
    }),

    /** Final standings for a tournament (GET /v1/tournaments/:id/results). */
    getTournamentResults: builder.query<TournamentResultsResponse, string>({
      query: (id) => `/v1/tournaments/${encodeURIComponent(id)}/results`,
    }),

    /**
     * Create a chess tournament (POST /v1/tournaments → 201). The host is the
     * caller (from the Bearer JWT); the body carries kind + clock + options.
     * Refreshes the tournament list so the new tournament shows immediately.
     */
    createTournament: builder.mutation<Tournament, CreateTournamentRequest>({
      query: (body) => ({ url: '/v1/tournaments', method: 'POST', body }),
      invalidatesTags: ['Tournaments'],
    }),

    /* ── Challenges (1v1 duels) ─────────────────────────────────────── */

    /** List the caller's chess challenges (GET /v1/challenges). */
    listChessChallenges: builder.query<ListChallengesResponse, ListChessChallengesQuery | void>({
      query: (params) => ({
        url: '/v1/challenges',
        params: params?.status ? { status: params.status } : undefined,
      }),
      providesTags: ['Challenges'],
    }),

    /** Single challenge by id (GET /v1/challenges/:id). Used to poll status. */
    getChessChallenge: builder.query<Challenge, string>({
      query: (id) => `/v1/challenges/${encodeURIComponent(id)}`,
      providesTags: ['Challenges'],
    }),

    /**
     * Create a 1v1 chess duel (POST /v1/challenges → 201). The challenger is
     * derived from the Bearer JWT; the body carries the opponent's Logto id +
     * format + clock. Returns the created challenge (status `pending`).
     */
    createChessChallenge: builder.mutation<Challenge, CreateChessChallengeRequest>({
      query: (body) => ({ url: '/v1/challenges', method: 'POST', body }),
      invalidatesTags: ['Challenges'],
    }),

    /** Opponent accepts a pending challenge (POST /v1/challenges/:id/accept). */
    acceptChessChallenge: builder.mutation<Challenge, string>({
      query: (id) => ({
        url: `/v1/challenges/${encodeURIComponent(id)}/accept`,
        method: 'POST',
      }),
      invalidatesTags: ['Challenges'],
    }),

    /** Opponent declines a pending challenge (POST /v1/challenges/:id/decline). */
    declineChessChallenge: builder.mutation<Challenge, string>({
      query: (id) => ({
        url: `/v1/challenges/${encodeURIComponent(id)}/decline`,
        method: 'POST',
      }),
      invalidatesTags: ['Challenges'],
    }),

    /* ── Brackets (knockout tournaments) ────────────────────────────── */

    /** List knockout brackets (GET /v1/brackets). Optional status filter. */
    listBrackets: builder.query<ListBracketsResponse, ListBracketsQuery | void>({
      query: (params) => ({
        url: '/v1/brackets',
        params: params?.status ? { status: params.status } : undefined,
      }),
      providesTags: ['Brackets'],
    }),

    /** Single bracket by id (GET /v1/brackets/:id). Polled while live. */
    getBracket: builder.query<Bracket, string>({
      query: (id) => `/v1/brackets/${encodeURIComponent(id)}`,
      providesTags: ['Brackets'],
    }),

    /** Join a bracket in registration (POST /v1/brackets/:id/join). */
    joinBracket: builder.mutation<Bracket, string>({
      query: (id) => ({ url: `/v1/brackets/${encodeURIComponent(id)}/join`, method: 'POST' }),
      invalidatesTags: ['Brackets'],
    }),

    /** Withdraw from a bracket in registration (DELETE /v1/brackets/:id/join). */
    leaveBracket: builder.mutation<Bracket, string>({
      query: (id) => ({ url: `/v1/brackets/${encodeURIComponent(id)}/join`, method: 'DELETE' }),
      invalidatesTags: ['Brackets'],
    }),

    // Bracket creation / start / cancel are admin-only (qchess:admin scope) and
    // live in the qchess-backoffice, not the TMA — TMA Logto tokens don't carry
    // that scope, so those endpoints are intentionally not wired here.

    /* ── Leaderboard ────────────────────────────────────────────────── */

    /** QLASH chess leaderboard (GET /v1/leaderboard). Optional paging. */
    getLeaderboard: builder.query<LeaderboardResponse, LeaderboardQuery | void>({
      query: (params) => ({
        url: '/v1/leaderboard',
        params: params
          ? {
              ...(params.page != null ? { page: params.page } : {}),
              ...(params.limit != null ? { limit: params.limit } : {}),
            }
          : undefined,
      }),
      keepUnusedDataFor: 120,
    }),
  }),
});

export const {
  useGetLinkQuery,
  useLazyStartLichessLinkQuery,
  useUnlinkLichessMutation,
  useListTournamentsQuery,
  useGetTournamentQuery,
  useGetTournamentResultsQuery,
  useCreateTournamentMutation,
  useListChessChallengesQuery,
  useGetChessChallengeQuery,
  useCreateChessChallengeMutation,
  useAcceptChessChallengeMutation,
  useDeclineChessChallengeMutation,
  useGetLeaderboardQuery,
  useListBracketsQuery,
  useGetBracketQuery,
  useJoinBracketMutation,
  useLeaveBracketMutation,
} = qchessApi;
